import React from 'react';
import useSWR from 'swr';
import { mutate as globalMutate } from 'swr';
import api from '../../services/api';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../services/paginatedList';
import type { SignatureAsset, SignatureValue } from '../../components/signature';
import type { CrewFile, CrewListItem, CrewLookups } from './types';

export const useCrewList = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<PaginatedApiResponse<CrewListItem> | CrewListItem[]>(
    `${moduleConfig.module.api.list}${q}`,
    fetcher,
    { revalidateOnFocus: true },
  );
  const normalized = normalizePaginatedListResponse<CrewListItem>(data, page, limit);
  return {
    data: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useCrewFile = (id?: number) => {
  const validId = typeof id === 'number' && Number.isFinite(id) && id > 0 ? id : undefined;
  const { data, error, isLoading, mutate } = useSWR<CrewFile>(
    validId ? moduleConfig.module.api.detail(validId) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading: Boolean(validId) && isLoading, isError: error, mutate };
};

export const useCrewLookups = () => {
  const { data, error, isLoading } = useSWR<CrewLookups>('/crew/lookups', fetcher, {
    revalidateOnFocus: false,
  });
  return { lookups: data, isLoading, isError: error };
};

function useCrewMediaBlobUrl(
  crewId: number | null | undefined,
  storageKey: string | null | undefined,
  kind: 'photo' | 'signature',
) {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const blobUrlRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!crewId || !storageKey) {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setBlobUrl(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get(`/crew/${crewId}/${kind}`, {
        responseType: 'blob',
        headers: { Accept: 'image/*,application/octet-stream' },
      })
      .then((res) => {
        if (cancelled) return;
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        const blob = res.data as Blob;
        if (res.status === 204 || !blob || blob.size === 0) {
          setBlobUrl(null);
          return;
        }
        const type = (blob.type || '').toLowerCase();
        if (type.includes('json') || type.includes('text/')) {
          setBlobUrl(null);
          return;
        }
        const imageBlob =
          type.startsWith('image/') || type === 'application/octet-stream' || !type
            ? new Blob([blob], { type: type.startsWith('image/') ? type : 'image/png' })
            : blob;
        const url = URL.createObjectURL(imageBlob);
        blobUrlRef.current = url;
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setBlobUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [crewId, storageKey, kind]);

  return { blobUrl, loading };
}

export function useCrewPhotoBlobUrl(
  crewId: number | null | undefined,
  photoStorageKey: string | null | undefined,
) {
  const { blobUrl, loading } = useCrewMediaBlobUrl(crewId, photoStorageKey, 'photo');
  return { photoBlobUrl: blobUrl, loading };
}

export function useCrewSignatureBlobUrl(
  crewId: number | null | undefined,
  signatureStorageKey: string | null | undefined,
) {
  const { blobUrl, loading } = useCrewMediaBlobUrl(crewId, signatureStorageKey, 'signature');
  return { signatureBlobUrl: blobUrl, loading };
}

async function revalidateCrew(id: number) {
  await globalMutate(`/crew/${id}`);
  await globalMutate((key) => typeof key === 'string' && key.startsWith('/crew'));
}

export async function uploadCrewPhoto(id: number, file: File) {
  const formData = new FormData();
  formData.append('file', file, file.name || 'photo.png');
  const res = await api.post(`/crew/${id}/photo`, formData);
  await revalidateCrew(id);
  return res.data as CrewFile;
}

export async function deleteCrewPhoto(id: number) {
  const res = await api.delete(`/crew/${id}/photo`);
  await revalidateCrew(id);
  return res.data as CrewFile;
}

export async function uploadCrewSignatureAsset(
  id: number,
  file: File,
  source?: 'draw' | 'upload',
): Promise<SignatureAsset> {
  const formData = new FormData();
  formData.append('file', file, file.name || 'signature.png');
  const q = source ? `?source=${encodeURIComponent(source)}` : '';
  const res = await api.post(`/crew/${id}/signature${q}`, formData);
  await revalidateCrew(id);
  const crew = res.data as CrewFile;
  if (!crew.signatureStorageKey) {
    throw new Error('Signature upload did not return storage metadata.');
  }
  return {
    storageKey: crew.signatureStorageKey,
    fileName: crew.signatureOriginalFilename || file.name,
    mimeType: crew.signatureMimeType || file.type || 'image/png',
    size: file.size,
  };
}

export async function deleteCrewSignature(id: number) {
  const res = await api.delete(`/crew/${id}/signature`);
  await revalidateCrew(id);
  return res.data as CrewFile;
}

export function crewSignatureValue(crew: CrewFile | null | undefined): SignatureValue | null {
  if (!crew?.signatureStorageKey) return null;
  return {
    storageKey: crew.signatureStorageKey,
    fileName: crew.signatureOriginalFilename || 'signature.png',
    mimeType: crew.signatureMimeType || 'image/png',
    size: 0,
    source:
      crew.signatureSource === 'draw' || crew.signatureSource === 'upload'
        ? crew.signatureSource
        : undefined,
    signedAt: crew.signatureSignedAt ?? undefined,
    subjectType: 'crew',
    subjectId: crew.id,
  };
}

export function crewSignatureDownloadPath(crewId: number, _storageKey: string) {
  return `/crew/${crewId}/signature`;
}
